import { AiHarnessVersion } from './ai-harness-version.js';
import { z } from 'zod';
/**
 * Phiên bản prompt trong DB — APM-H-f.
 *
 * `skill.systemPrompt(context)` trong code là **mặc định**. Một bản ghi đè
 * thay nó mà không cần deploy; placeholder `{{dynamic}}` là chỗ chèn phần động
 * do code dựng (WBS template của `generate_plan`…). Vòng đời một chiều:
 * `draft` → `active` → `retired`; không sửa, không xoá — mỗi lần đổi là một
 * phiên bản mới, và các hàng là dấu vết.
 */
export const DYNAMIC_PLACEHOLDER = '{{dynamic}}';
export const AiPromptStatus = z.enum(['draft', 'active', 'retired']);
/**
 * Bản ghi bằng chứng của một lần kích hoạt prompt — APM-H-ao (mượn
 * `has_experience` / `EXPERIENCE.md` của Harness2).
 *
 * Golden xanh nói "không hỏng", không nói "vì sao đổi" và "đổi dựa trên gì".
 * Nên kích hoạt một bản prompt phải kèm: *khi* gặp dấu hiệu gì (`cue`), *thì*
 * sửa gì (`lesson`), *vì* run nào cho thấy (`evidence.runIds` — run **sandbox**
 * của đúng skill; trace đã ở DB, bản ghi chỉ trỏ tới, không chép nội dung), và
 * người viết tin tới đâu (`confidence`). `scope` là quyết định của người viết:
 * `invariant` = bài học đúng cho mọi skill/miền, `domain` = chỉ cho skill này
 * (APM-H-ar sẽ dùng). Thiếu bất kỳ trường nào là **không kích hoạt được**
 * (`AI_PROMPT_NO_EVIDENCE`) — một luật, không một lời khuyên. Bản nháp thì
 * không cần: giữ đường soạn nhanh, cửa chặn ở kích hoạt.
 */
export const PromptExperienceConfidence = z.enum(['low', 'medium', 'high']);
export const PromptExperienceScope = z.enum(['invariant', 'domain']);
/** Tóm tắt một lượt golden — đủ để so hai bản, không chép từng case (case nằm ở run). */
export const PassSummary = z.object({
    ran: z.number().int().nonnegative(),
    /** Pass^k: case mà mọi lượt đều đạt. */
    passed: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    k: z.number().int().positive().default(1),
});
/**
 * Golden của bản được kích hoạt (`after`) so với bản active ngay trước đó
 * (`before`). `before` là `after` **đã lưu** của bản trước — golden không được
 * lưu riêng, nên bản trước chưa có bản ghi (hoặc skill chạy prompt trong code)
 * ⇒ `null`, không chặn.
 */
export const PromptGoldenDiff = z.object({
    before: PassSummary.nullable(),
    after: PassSummary,
});
/**
 * Giá trị đặc biệt của `promptVersion` ở run **sandbox** — APM-H-au: "prompt
 * trong code, bỏ qua cả bản nháp của người bấm". Không ghim gì thì `PromptHook`
 * ở sandbox lấy nháp mới nhất của người bấm, và `harness:propose` lần live 3
 * (23/10) đã đo "gốc" trên một nháp bất biến còn sót từ lần trước. Gốc phải là
 * bản đang dùng thật: `active` nếu có, không thì chuỗi trong code — chưa bao
 * giờ là nháp. Dấu `@` để không trùng dạng `v<n>` của bản ghi đè thật; bước
 * `policy` vẫn ghi `promptVersion: null` như một run không ghi đè.
 */
export const PROMPT_VERSION_CODE = '@code';
export const PromptExperience = z.object({
    cue: z.string().trim().min(1).max(500),
    lesson: z.string().trim().min(1).max(2_000),
    evidence: z.object({
        runIds: z.array(z.uuid()).min(1).max(50),
        /** Server tự điền lúc kích hoạt khi golden của bản nháp đã chạy; người gửi không cần. */
        goldenDiff: PromptGoldenDiff.optional(),
    }),
    confidence: PromptExperienceConfidence,
    scope: PromptExperienceScope,
});
export const AiPromptVersion = z.object({
    id: z.uuid(),
    skill: z.string(),
    /** `v1`, `v2`… tăng theo skill. Ghi vào `ai_run.prompt_version`. */
    version: z.string(),
    status: AiPromptStatus,
    systemPrompt: z.string(),
    /** Ghi đè message người dùng đầu; `{{dynamic}}` = message code dựng. Bỏ trống = code. */
    userTemplate: z.string().nullable(),
    notes: z.string().nullable(),
    createdBy: z.uuid(),
    createdAt: z.iso.datetime(),
    activatedBy: z.uuid().nullable(),
    activatedAt: z.iso.datetime().nullable(),
    retiredBy: z.uuid().nullable(),
    retiredAt: z.iso.datetime().nullable(),
    /** Lý do admin ép kích hoạt khi golden set đỏ — APM-H-n. `null` = qua kiểm bình thường. */
    forcedReason: z.string().nullable().optional(),
    /** Bản ghi bằng chứng — APM-H-ao. `null` ở bản nháp chưa khai, hoặc bản kích hoạt trước APM-H-ao. */
    experience: PromptExperience.nullable().optional(),
});
export const AiPromptList = z.object({
    skill: z.string(),
    /** Prompt trong code, để so với bản ghi đè. Phần động chưa chèn. */
    codeDefault: z.string(),
    /** Skill có `prepare()` ⇒ bản ghi đè bắt buộc có `{{dynamic}}`. */
    requiresDynamic: z.boolean(),
    activeVersion: z.string().nullable(),
    versions: z.array(AiPromptVersion),
});
export const CreatePromptDraftInput = z.object({
    systemPrompt: z.string().min(20).max(50_000),
    userTemplate: z.string().min(1).max(20_000).optional(),
    notes: z.string().max(500).optional(),
    /** Khai sẵn bằng chứng lúc soạn (tuỳ chọn) — kích hoạt sau không cần gửi lại. */
    experience: PromptExperience.optional(),
});
/** Kết quả quét tĩnh một bản prompt — chặn thì không lưu, cảnh báo thì lưu kèm. */
export const PromptScanResult = z.object({
    blockers: z.array(z.object({ path: z.string(), message: z.string() })),
    warnings: z.array(z.string()),
});
/**
 * Tripwire: thước đo không được lọt vào thứ model đọc — APM-H-an (mượn
 * `BenchPrompts.__post_init__` của Harness2).
 *
 * Golden set khẳng định **tính chất** của output. Một người đọc ca hỏng rồi
 * chép nguyên khẳng định ấy vào prompt ghi đè, `org_knowledge` hay ghi chú bộ
 * nhớ là dạy prompt cách qua bài thi thay vì làm việc — và từ đó golden không
 * đo được gì nữa. Ba thứ bị bắt: tên ca golden, nguyên văn một khẳng định, và
 * các từ dưới đây.
 *
 * Danh sách là **đóng** (cùng lý do với taxonomy lớp lỗi): mở thì sau ba tháng
 * không ai biết nó chặn gì. Toàn chữ Anh kỹ thuật — không bắt tiếng Việt
 * thường dùng trong prompt nghiệp vụ ("đánh giá", "kiểm tra"). So không phân
 * biệt hoa thường, theo nguyên từ (`benchmarks` vẫn bắt, `goldenrod` thì không).
 */
export const MEASUREMENT_LEAK_TERMS = [
    'golden',
    'Pass^k',
    'holdout',
    'release gate',
    'benchmark',
];
/** `case` = tên ca golden; `assertion` = nguyên văn khẳng định; `term` = từ trong `MEASUREMENT_LEAK_TERMS`. */
export const MeasurementLeakKind = z.enum(['case', 'assertion', 'term']);
/** Một chỗ rò: chuỗi nào, ở trường nào — `details.leaks[]` của `AI_MEASUREMENT_LEAK`. */
export const MeasurementLeak = z.object({
    path: z.string(),
    kind: MeasurementLeakKind,
    match: z.string(),
});
export const CreatePromptDraftResult = z.object({
    version: AiPromptVersion,
    warnings: z.array(z.string()),
});
/** Tác động của việc kích hoạt: cache prefix của skill này mất trên mọi tổ chức. */
export const PromptActivationImpact = z.object({
    skill: z.string(),
    /** Số lượt gọi model của skill trong 24h qua — "bấm tiếp là bấy nhiêu lượt mất cache". */
    callsLast24h: z.number().int().nonnegative(),
    runsLast24h: z.number().int().nonnegative(),
});
/**
 * Loại một case golden — APM-H-n, thêm `false-refusal` ở APM-H-ab.
 *
 * `false-refusal` canh **chiều hỏng thứ hai** của một hàng rào: không phải "yêu
 * cầu xấu có bị chặn không" mà "yêu cầu hợp lệ có làm được không". Nó cần thiết
 * vì `HARNESS_RULES` dặn khá mạnh về cách từ chối, và `finalize` có quyền loại
 * từng phần — hai thứ cùng đẩy về phía im lặng, mà không chỉ số nào đang đo.
 *
 * `prefix` (APM-H-ac) là ca hồi quy **theo tiền tố dấu vết**: đóng băng lịch sử
 * ngay trước bước sai đầu tiên rồi chỉ kiểm **một** hành động kế tiếp. Rẻ hơn
 * hẳn ca đầu-cuối và cô lập đúng một quyết định — với một hệ cần độ tin cậy
 * cao, bộ tiền tố quan trọng hơn bộ đầu-cuối.
 */
export const GoldenKind = z.enum(['clean', 'prompt-injection', 'edge', 'false-refusal', 'prefix']);
/**
 * Kết cục một case golden — APM-H-n. `failures` là tên khẳng định đỏ (message
 * của `expect`), gộp từ mọi lượt và khử trùng.
 *
 * `ok` là **Pass^k**: mọi lượt đều đạt — APM-H-ab. Một case đạt 4/5 lượt là một
 * case **đỏ**, vì nghiệp vụ ở đây không có chỗ cho "thường thì đúng".
 */
export const GoldenCaseResult = z.object({
    name: z.string(),
    kind: GoldenKind,
    ok: z.boolean(),
    failures: z.array(z.string()),
    runId: z.uuid().nullable(),
    /** Số lượt đã chạy cho case này (`k`). */
    attempts: z.number().int().positive().default(1),
    /** Số lượt đạt. `ok` ⟺ `passedAttempts === attempts`. */
    passedAttempts: z.number().int().nonnegative().default(1),
    /** Mọi `runId` của case, theo thứ tự chạy — lượt đỏ tra được dấu vết. */
    runIds: z.array(z.uuid()).default([]),
});
/**
 * So **ghép cặp** hai bản prompt trên cùng bộ case — APM-H-ab.
 *
 * Trừ hai tỉ lệ độc lập ("bản mới 80 %, bản cũ 70 %") là cách sai: hai lượt
 * chạy khác nhau khác cả về case dễ/khó lẫn về may rủi của model. Ghép cặp thì
 * mỗi case được chạy dưới **cả hai** bản, và thứ đáng đọc là bốn ô của
 * McNemar — đặc biệt hai ô lệch: `onlyA` (bản cũ đạt, bản mới hỏng) là **hồi
 * quy**, `onlyB` là **cải thiện**. `both`/`neither` không mang thông tin về
 * chênh lệch, nên không được đưa vào phép so.
 */
export const GoldenComparison = z.object({
    /** Bản đem ra so (B). `null` = bản đang kích hoạt. */
    promptVersion: z.string().nullable(),
    cases: z.array(z.object({
        name: z.string(),
        attempts: z.number().int().positive(),
        aPassed: z.number().int().nonnegative(),
        bPassed: z.number().int().nonnegative(),
    })),
    both: z.number().int().nonnegative(),
    neither: z.number().int().nonnegative(),
    /** Chỉ A đạt (Pass^k) — **hồi quy** nếu B là bản mới. */
    onlyA: z.number().int().nonnegative(),
    /** Chỉ B đạt — cải thiện. */
    onlyB: z.number().int().nonnegative(),
});
/**
 * Kết quả cả bộ. Hai con số **khác nhau**, cố ý báo cả hai — APM-H-ab:
 *
 * - `passAtK`: số case có **ít nhất một** lượt đạt. Đây là trần năng lực: "làm
 *   được không".
 * - `passed` (= Pass^k): số case có **mọi** lượt đạt. Đây là độ tin cậy: "làm
 *   được đều không". Với `p = 0,6`, `k = 5` thì hai số là 99 % và 7,8 % — báo
 *   nhầm số là kết luận ngược hẳn.
 */
export const GoldenReport = z.object({
    ran: z.number().int().nonnegative(),
    passed: z.number().int().nonnegative(),
    cases: z.array(GoldenCaseResult),
    /** Số lượt mỗi case. `1` = như trước APM-H-ab (và là mặc định của `fixture`). */
    k: z.number().int().positive().default(1),
    /** Tổng số lượt đã chạy = `ran × k`. Đây là `n` của mọi tỉ lệ dưới đây. */
    attempts: z.number().int().nonnegative().default(0),
    passAtK: z.number().int().nonnegative().default(0),
    /**
     * Tỉ lệ **lượt** đạt và sai số chuẩn `√(p(1−p)/n)` — APM-H-ab.
     *
     * Có mặt để không ai đọc "70 %" mà quên `n = 3`: với `n` nhỏ, khoảng tin cậy
     * rộng hơn mọi khác biệt ta định kết luận.
     */
    rate: z
        .object({
        p: z.number().min(0).max(1),
        n: z.number().int().nonnegative(),
        se: z.number().min(0),
    })
        .default({ p: 0, n: 0, se: 0 }),
    /** Có khi người gọi xin so ghép cặp với một bản prompt khác — APM-H-ab. */
    compare: GoldenComparison.nullable().default(null),
    /**
     * Case bị bỏ qua vì **chỉ đúng ở fixture** (APM-H-benchdata-a): case kịch bản cả hành vi
     * sai của model để kiểm code xử lý nó, hoặc cần dữ liệu trái ngược với các case khác.
     * Chạy live thì model làm đúng và case ấy đỏ — nói ra thay vì im lặng thu nhỏ bộ case.
     */
    fixtureOnly: z.array(z.object({ name: z.string(), reason: z.string() })).default([]),
});
/** Sai số chuẩn của một tỉ lệ nhị phân — `√(p(1−p)/n)`. `n = 0` ⇒ `0` (không đo được). */
export function standardError(p, n) {
    return n > 0 ? Math.sqrt((p * (1 - p)) / n) : 0;
}
/**
 * Kích hoạt một phiên bản — APM-H-n chạy golden set `live` của skill trước.
 * `projectId`: dự án thử để chạy (bắt buộc khi skill có golden case và không `force`).
 * `force.reason`: ép qua khi đỏ hoặc khi không chạy được (chưa có key); ghi vào phiên bản.
 */
export const ActivatePromptBody = z.object({
    projectId: z.uuid().optional(),
    force: z.object({ reason: z.string().min(5).max(500) }).optional(),
    /**
     * Bản ghi bằng chứng — APM-H-ao. Gửi ở đây **thắng** bản đã lưu trên nháp;
     * không gửi thì dùng bản trên nháp; cả hai đều không có ⇒ `AI_PROMPT_NO_EVIDENCE`.
     * `force` chỉ bỏ qua golden, không bỏ qua bằng chứng.
     */
    experience: PromptExperience.optional(),
});
/** Chạy golden set của một skill từ trang Harness — `POST /ai/harness/golden/:skill/run`. */
export const RunGoldenBody = z.object({
    harnessVersion: AiHarnessVersion.optional(),
    projectId: z.uuid(),
    /** Chỉ chạy một case (theo `name`). Bỏ trống = cả bộ. */
    case: z.string().max(120).optional(),
    promptVersion: z.string().max(40).optional(),
    /**
     * Số lượt mỗi case — APM-H-ab. `1` (mặc định) giữ nguyên hành vi cũ; `≥ 2`
     * cho Pass^k. Trần 10 vì mỗi lượt là tiền thật: 17 skill × 4 case × 10 lượt
     * là một hoá đơn, không phải một nút bấm.
     */
    k: z.number().int().min(1).max(10).default(1),
    /**
     * So **ghép cặp** với một bản prompt khác — APM-H-ab. Mỗi case chạy `k` lượt
     * dưới bản hiện chọn **và** `k` lượt dưới bản này, trên cùng dữ liệu thử.
     * Tốn gấp đôi, nên là tuỳ chọn chứ không mặc định.
     */
    comparePromptVersion: z.string().max(40).optional(),
    /**
     * Chạy cả bộ trên **model khác** — APM-H-bench. Id đúng như gateway gọi (phải
     * có dòng giá). Bỏ trống = tầng của skill, như production.
     */
    model: z.string().trim().min(1).max(200).optional(),
    /** Mức suy nghĩ ghi đè cho cả bộ — APM-H-bench. */
    reasoning: z.enum(['off', 'low', 'medium', 'high', 'adaptive']).optional(),
    /**
     * Người gọi đang chạy **fixture** (client model là kịch bản) — APM-H-benchdata-a. Server
     * không phân biệt được model thật hay giả, nên người gọi nói. `true` ⇒ chạy cả case
     * `fixtureOnly`; mặc định (live) ⇒ báo chúng trong `GoldenReport.fixtureOnly`, không chạy.
     */
    fixture: z.boolean().default(false),
});
export const ActivatePromptResult = z.object({
    version: AiPromptVersion,
    impact: PromptActivationImpact,
    /** Kết quả golden set đã chạy; `null` khi skill không có case hoặc bị ép qua mà không chạy được. */
    golden: GoldenReport.nullable(),
});
/**
 * Một mục của thư viện bài học — `GET /ai/harness/experience` (APM-H-ao). Mọi
 * bản **đã từng** kích hoạt (kể cả đã bị thay thế), mới nhất trước: đây là
 * `INDEX.md` của ta — đầu vào cho `Đánh giá harness` §5 bước 4/5 và cho APM-H-aq.
 */
export const PromptExperienceEntry = z.object({
    skill: z.string(),
    version: z.string(),
    status: AiPromptStatus,
    activatedAt: z.iso.datetime(),
    cue: z.string(),
    lesson: z.string(),
    scope: PromptExperienceScope,
    confidence: PromptExperienceConfidence,
    runIds: z.array(z.uuid()),
    goldenDiff: PromptGoldenDiff.nullable(),
});
export const PromptExperienceQuery = z.object({
    skill: z.string().trim().min(1).max(60).optional(),
    scope: PromptExperienceScope.optional(),
});
export const PromptExperienceList = z.object({ items: z.array(PromptExperienceEntry) });
/**
 * Bài học **lặp ở ≥ 2 skill** — `GET /ai/harness/experience/recurring` (APM-H-ar).
 *
 * Đầu vào cho `Đánh giá harness` §5 bước 4: một bài học `invariant` mà hai
 * skill cùng phải học là dấu hiệu chỗ sửa nằm ở **lớp 1** (luật harness,
 * `defaults.ts`, engine bản kế), không phải ở prompt từng skill. Hai nguồn:
 * - `experience`: bản prompt đã kích hoạt có `cue` **chuẩn hoá** giống nhau
 *   (chữ thường, bỏ dấu câu, gộp khoảng trắng) ở ≥ 2 skill;
 * - `attribution`: quy kết lỗi cùng `scope` và cùng lớp lỗi ở ≥ 2 skill trong
 *   cửa sổ `days` (mặc định 30).
 *
 * Endpoint chỉ **gom**, không đề xuất: chuyển một bài học vào lớp 1 là quyết
 * định của người (cache của mọi skill vỡ, mọi golden chạy lại).
 */
export const RecurringLessonQuery = z.object({
    scope: PromptExperienceScope.default('invariant'),
    days: z.coerce.number().int().min(1).max(365).default(30),
});
export const RecurringLesson = z.discriminatedUnion('kind', [
    z.object({
        kind: z.literal('experience'),
        /** `cue` đã chuẩn hoá — khoá gom. */
        key: z.string(),
        scope: PromptExperienceScope,
        /** Tên skill, không trùng, sắp theo chữ — luôn ≥ 2. */
        skills: z.array(z.string()).min(2),
        items: z.array(z.object({
            skill: z.string(),
            version: z.string(),
            cue: z.string(),
            lesson: z.string(),
            activatedAt: z.iso.datetime(),
        })),
    }),
    z.object({
        kind: z.literal('attribution'),
        /** Lớp lỗi — khoá gom. */
        key: z.string(),
        scope: PromptExperienceScope,
        skills: z.array(z.string()).min(2),
        items: z.array(z.object({
            skill: z.string(),
            runId: z.uuid(),
            recordedAt: z.iso.datetime(),
        })),
    }),
]);
export const RecurringLessonList = z.object({ items: z.array(RecurringLesson) });
//# sourceMappingURL=ai-prompt.js.map